import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, IconButton, Typography, useMediaQuery } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import AdditionalServiceDialog from './AdditionalServiceDialog';
import Selector from '../../common/Selector';
import {
    PriceText,
    DurationText,
    DurationContainer,
    ClockIcon,
    PriceChevronContainer,
    ChevronIcon
} from '../common/StyledText';
import { fetchPractitioners } from '../../../api/practitionerApi';
import {
    changeStep,
    // changeCurrentAppointments,
    setCurrentCategory,
    changeService,
    updateAppointments,
    availableStaff
} from '../../../redux/actions/book-appointment';
import {
    getCurrentServices,
    getCurrentAppointments,
    getCurrentLocation,
    getCourseContext
} from '../../../redux/selectors/book-appointment';
import { TIME, SERVICE } from '../../../constants/steps';
import { MOBILE } from '../../../constants/breakpoints';
// import { STAFF_IO_FROM_FRONT } from '../../../constants/socket';

import styles from './styles';
import { Info } from '@material-ui/icons';
import InfoModal from '../service/InfoModal';
import { getCurrentTimezonedDate } from '../../../collums-components/helpers/timezone';
import { getCurrencySymbol } from '../../../collums-components/helpers';
import { toastr } from 'react-redux-toastr';
import { getLocationItem } from '../../../collums-constants/utils';
import { DEFAULT_PHOTO } from '../../../collums-constants';

function Staff({ classes }) {
    const [staff, setStaff] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isMobile = useMediaQuery(`(max-width:${MOBILE}px)`);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [minValue, setMinValue] = useState(0);
    const [maxValue, setMaxValue] = useState(0);
    const history = useHistory();

    const currentLocation = useSelector(getCurrentLocation);
    const currentServices = useSelector(getCurrentServices);
    const currentAppointments = useSelector(getCurrentAppointments);
    const courseContext = useSelector(getCourseContext);
    const isReschedule = useSelector(state => state.bookAppointment.isReschedule);
    const currentAppointment = currentAppointments?.[currentAppointments.length - 1];
    const effectiveService = currentAppointment?.service || currentServices;

    const [modalProps, setModalProps] = useState({
        title: '',
        content: '',
        isVisible: false
    });

    const dispatch = useDispatch();

    const handleDialogConfirm = useCallback(() => {
        setDialogVisible(false);
        dispatch(changeStep(SERVICE));
        dispatch(setCurrentCategory(null));
        dispatch(changeService(null));
        history.push(
            window.location.pathname
                .split('/')
                .slice(0, 3)
                .join('/') +
                '?service=' +
                currentAppointments.length
        );
    }, [dispatch, history, currentAppointments]);

    const scrollToSteps = () => {
        const stepElement = document.querySelector('.MuiStepper-root');
        if (stepElement) {
            setTimeout(() => {
                stepElement.scrollIntoView({ behavior: 'instant', block: 'start' });
            }, 100);
        }
    };

    const handleDialogCancel = useCallback(() => {
        setDialogVisible(false);
        dispatch(changeStep(TIME));
        dispatch(setCurrentCategory(null));
        dispatch(changeService(null));
    }, [dispatch]);

    const openModal = data => {
        setModalProps({
            isVisible: true,
            title: data.title,
            content: data.content
        });
    };

    const handleInfoClick = (event, practitioner) => {
        event.stopPropagation();
        openModal({
            isVisible: true,
            content: practitioner.description,
            title: practitioner.displayName
        });
    };

    const getPractitionerAvatar = practitioner => {
        const defaultAvatar =
            practitioner.gender === 'Female' ? DEFAULT_PHOTO.DEFAULT_FEMALE : DEFAULT_PHOTO.DEFAULT_MALE;

        // Check if avatar exists and process it similar to PINModal
        if (practitioner.avatar) {
            // If the avatar is a full URL, use it directly
            if (practitioner.avatar.startsWith('http')) {
                return practitioner.avatar;
            }
            // If it's a path, construct the imgix URL
            return (
                'https://collums.imgix.net/' + practitioner.avatar.split('/').pop() + '?w=47&h=47&mask=ellipse&fit=crop'
            );
        }

        return defaultAvatar;
    };

    const handleStaffChange = useCallback(
        staff => {
            if (!currentAppointments || currentAppointments.length === 0) return;

            const tempCurrAppts = [...currentAppointments];
            const currentIndex = tempCurrAppts.length - 1;

            if (currentIndex >= 0) {
                const currentService = tempCurrAppts[currentIndex].service || {};
                const isRegularServiceBooking = currentService._isRegularServiceBooking;

                tempCurrAppts[currentIndex] = {
                    ...tempCurrAppts[currentIndex],
                    service: {
                        ...currentService,
                        defaultPrice: staff?.grossPrice || minValue,
                        ...(staff?.duration && {
                            defaultDuration: staff.duration
                        }),
                        _isRegularServiceBooking: isRegularServiceBooking
                    },
                    staff: staff
                };

                dispatch(updateAppointments(tempCurrAppts));

                if (!isReschedule) {
                    setDialogVisible(true);
                } else {
                    handleDialogCancel();
                }
            }

            // handleDialogCancel();
        },
        [currentAppointments, dispatch, minValue, isReschedule, handleDialogCancel]
    );

    useEffect(() => {
        const fetch = async () => {
            if (!currentServices || !currentLocation.id) return;

            if (currentServices?.locations) {
                for (const loc of currentServices.locations) {
                    if (!loc.clinic) loc.clinic = currentLocation.id;
                }
            }

            try {
                setIsLoading(true);

                const payload = {
                    month: getCurrentTimezonedDate().getMonth(),
                    clinic: currentLocation.id,
                    service: currentServices
                };
                const data = await fetchPractitioners(payload);

                dispatch(availableStaff(data));

                if (data.length === 1) {
                    handleStaffChange(data[0]);
                }

                setStaff(data);
                scrollToSteps();
            } catch (err) {
                toastr.error(err?.data?.message || 'Something went wrong');

                return;
            } finally {
                setIsLoading(false);
            }
        };

        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation.id, currentServices?.id, dispatch]);

    useEffect(() => {
        if (!staff || !staff.length) return;

        const values = staff.map(practitioner => practitioner.grossPrice);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);

        setMinValue(minValue);
        setMaxValue(maxValue);
    }, [staff]);

    const durations = staff.map(practitioner => practitioner.duration);
    const minDuration = Math.min(...durations);
    const differentDurations = new Set(durations).size > 1;

    const haveCurrentServiceAndLocation = currentServices && currentLocation;
    const haveLocations = currentServices?.locations
        ? getLocationItem(currentServices, currentLocation.id)
        : currentServices?.location;
    const item = haveCurrentServiceAndLocation ? haveLocations : null;
    const defaultDuration = item?.defaultDuration || currentServices?.defaultDuration || 0;

    const currentAppointmentIndex = currentAppointments?.length ? currentAppointments.length - 1 : -1;
    const currentAppointmentBeingConfigured =
        currentAppointmentIndex >= 0 ? currentAppointments[currentAppointmentIndex] : null;

    const assignedStaff = currentAppointmentBeingConfigured?.staff;
    const hasAssignedStaff = assignedStaff && assignedStaff.id;

    return (
        <Box className={classes.selectorContainer}>
            {!isLoading ? (
                <>
                    {((isMobile && !dialogVisible) || !isMobile) && (
                        <>
                            {staff.length ? (
                                <Selector className={['overflowVisible', classes.selector].join(' ')}>
                                    {hasAssignedStaff ? (
                                        <Selector.Row>
                                            <Selector.Column>
                                                <Grid container justify="space-between" alignItems="center">
                                                    <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                                        <img
                                                            src={getPractitionerAvatar(assignedStaff)}
                                                            alt={`${assignedStaff.displayName} avatar`}
                                                            className={classes.practitionerAvatar}
                                                        />
                                                        <div>
                                                            <Typography variant="h4">
                                                                {assignedStaff.displayName}
                                                            </Typography>
                                                            {currentServices?.showDurationOnline && (
                                                                <DurationContainer>
                                                                    <ClockIcon />
                                                                    <DurationText>
                                                                        {assignedStaff.duration || defaultDuration || 0}{' '}
                                                                        mins
                                                                    </DurationText>
                                                                </DurationContainer>
                                                            )}
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            </Selector.Column>
                                            <PriceChevronContainer>
                                                {currentServices?.showPriceOnline && (
                                                    <PriceText>
                                                        {(() => {
                                                            const serviceId =
                                                                effectiveService?.id || effectiveService?._id;
                                                            const isCourseServiceId =
                                                                courseContext?.courseServices &&
                                                                serviceId &&
                                                                courseContext.courseServices.includes(serviceId);
                                                            const isRegularServiceBooking =
                                                                effectiveService?._isRegularServiceBooking === true;

                                                            return isCourseServiceId && !isRegularServiceBooking ? (
                                                                `${getCurrencySymbol()}0.00`
                                                            ) : (
                                                                <>
                                                                    {getCurrencySymbol()}
                                                                    {parseFloat(assignedStaff.grossPrice || 0).toFixed(
                                                                        2
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </PriceText>
                                                )}
                                            </PriceChevronContainer>
                                        </Selector.Row>
                                    ) : (
                                        // Show "Any practitioner" only if no staff is assigned and multiple staff available
                                        staff.length > 1 && (
                                            <Selector.Row onClick={() => handleStaffChange(null)}>
                                                <Selector.Column>
                                                    <Grid container justify="space-between">
                                                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                                                            <img
                                                                src="/icons/any-practitioner.svg"
                                                                alt="Any practitioner"
                                                                className={classes.anyPractitionerIcon}
                                                            />
                                                            <div>
                                                                <Typography variant="h4">Any practitioner</Typography>
                                                                {currentServices?.showDurationOnline && (
                                                                    <DurationContainer>
                                                                        <ClockIcon />
                                                                        <DurationText>
                                                                            {differentDurations
                                                                                ? 'From ' + minDuration
                                                                                : defaultDuration || 0}{' '}
                                                                            mins
                                                                        </DurationText>
                                                                    </DurationContainer>
                                                                )}
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                </Selector.Column>
                                                <PriceChevronContainer>
                                                    {currentServices?.showPriceOnline && (
                                                        <PriceText>
                                                            {(() => {
                                                                const serviceId =
                                                                    effectiveService?.id || effectiveService?._id;
                                                                const isCourseServiceId =
                                                                    courseContext?.courseServices &&
                                                                    serviceId &&
                                                                    courseContext.courseServices.includes(serviceId);
                                                                const isRegularServiceBooking =
                                                                    effectiveService?._isRegularServiceBooking === true;

                                                                return isCourseServiceId && !isRegularServiceBooking ? (
                                                                    `${getCurrencySymbol()}0.00`
                                                                ) : (
                                                                    <>
                                                                        {minValue !== maxValue ? 'From ' : ''}
                                                                        {getCurrencySymbol()}
                                                                        {parseFloat(minValue).toFixed(2)}
                                                                    </>
                                                                );
                                                            })()}
                                                        </PriceText>
                                                    )}
                                                    <ChevronIcon />
                                                </PriceChevronContainer>
                                            </Selector.Row>
                                        )
                                    )}
                                    {!hasAssignedStaff && <Divider />}
                                    {!hasAssignedStaff &&
                                        staff.map(practitioner => (
                                            <React.Fragment key={practitioner.id}>
                                                <Selector.Row
                                                    onClick={() => handleStaffChange(practitioner)}
                                                    className={classes.selectorColumn}
                                                >
                                                    <Selector.Column>
                                                        <Grid container justify="space-between" alignItems="center">
                                                            <Grid
                                                                item
                                                                style={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <img
                                                                    src={getPractitionerAvatar(practitioner)}
                                                                    alt={`${practitioner.displayName} avatar`}
                                                                    className={classes.practitionerAvatar}
                                                                />
                                                                <div>
                                                                    <Typography variant="h4">
                                                                        {practitioner.displayName}
                                                                    </Typography>
                                                                    {practitioner.description && (
                                                                        <IconButton
                                                                            onClick={event =>
                                                                                handleInfoClick(event, practitioner)
                                                                            }
                                                                            className={classes.info}
                                                                        >
                                                                            <Info />
                                                                        </IconButton>
                                                                    )}
                                                                    {currentServices?.showDurationOnline && (
                                                                        <DurationContainer>
                                                                            <ClockIcon />
                                                                            <DurationText>
                                                                                {practitioner.duration} mins
                                                                            </DurationText>
                                                                        </DurationContainer>
                                                                    )}
                                                                </div>
                                                            </Grid>
                                                        </Grid>
                                                    </Selector.Column>
                                                    <PriceChevronContainer>
                                                        {currentServices?.showPriceOnline && (
                                                            <PriceText>
                                                                {(() => {
                                                                    const serviceId =
                                                                        effectiveService?.id || effectiveService?._id;
                                                                    const isCourseService =
                                                                        courseContext?.courseServices &&
                                                                        serviceId &&
                                                                        courseContext.courseServices.includes(
                                                                            serviceId
                                                                        );
                                                                    const isRegularServiceBooking =
                                                                        effectiveService?._isRegularServiceBooking ===
                                                                        true;

                                                                    return isCourseService &&
                                                                        !isRegularServiceBooking ? (
                                                                        `${getCurrencySymbol()}0.00`
                                                                    ) : (
                                                                        <>
                                                                            {getCurrencySymbol()}
                                                                            {parseFloat(
                                                                                practitioner.grossPrice || 0
                                                                            ).toFixed(2)}
                                                                        </>
                                                                    );
                                                                })()}
                                                            </PriceText>
                                                        )}
                                                        <ChevronIcon />
                                                    </PriceChevronContainer>
                                                </Selector.Row>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                </Selector>
                            ) : (
                                <div style={{ padding: 20, textAlign: 'center' }}>
                                    <Typography>This service is currently unavailable</Typography>
                                </div>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className={classes.loading}>
                    <CircularProgress />
                </div>
            )}

            <AdditionalServiceDialog
                open={dialogVisible}
                onConfirm={handleDialogConfirm}
                onCancel={handleDialogCancel}
            />
            <InfoModal
                setIsVisible={setModalProps}
                isVisible={modalProps.isVisible}
                title={modalProps.title}
                content={modalProps.content}
            />
        </Box>
    );
}

Staff.propTypes = {
    classes: PropTypes.object.isRequired
    // serviceIndex: PropTypes.number
};

export default withStyles(styles)(Staff);
